/**
 * L'identité visuelle du tenant, appliquée à l'interface.
 *
 * Une couleur d'accent choisie par le client ne peut pas être posée telle
 * quelle : l'interface l'utilise en **fond**, avec du texte par-dessus — le
 * bouton principal, la pastille de la barre latérale, les puces de statut.
 * Ce texte était `text-bg-primary`, c'est-à-dire l'encre sombre, écrite en
 * dur : une entreprise choisissant un bleu marine aurait obtenu du texte
 * noir sur fond noir — un bouton invisible, sans aucun avertissement, sur
 * *son* écran seulement. Il est devenu `text-accent-contrast`, calculé ici.
 *
 * D'où deux valeurs dérivées de la couleur plutôt qu'une seule posée : la
 * couleur de texte lisible par-dessus, et la teinte de survol.
 */
import { DEFAULT_PRIMARY_COLOR } from "@dker/core";

/** Le fond sombre de l'application, cible par défaut du texte sur accent. */
const INK = "#101215";
const PAPER = "#FFFFFF";

/**
 * Les deux fonds de page, un par thème.
 *
 * Depuis que le thème clair existe, juger une couleur sur le seul fond sombre
 * ne suffit plus : un jaune pâle ressort parfaitement sur du graphite
 * #16181C et disparaît sur du papier #F5F5F3. Le même utilisateur peut voir les deux.
 */
const FONDS = ["#16181C", "#F5F5F3"] as const;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = Number.parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** Luminance relative WCAG. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * La couleur de texte la plus lisible sur un fond donné.
 *
 * On ne devine pas : on compare le contraste réel de l'encre sombre et du
 * blanc sur cette couleur, et on garde le meilleur des deux.
 */
export function readableTextOn(hex: string): string {
  const bg = parseHex(hex);
  if (!bg) return INK;
  const ink = parseHex(INK)!;
  const paper = parseHex(PAPER)!;
  return contrast(bg, ink) >= contrast(bg, paper) ? INK : PAPER;
}

/**
 * La teinte de survol.
 *
 * Éclaircir marche pour l'ambre par défaut, et échoue sur un jaune vif — qui
 * saturerait au blanc sans qu'on voie rien bouger. On s'éloigne donc du blanc
 * quand la couleur est déjà claire, et on s'en rapproche sinon : le survol
 * reste perceptible dans les deux cas.
 */
export function hoverVariant(hex: string, amount = 0.12): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const versLeClair = luminance(rgb) < 0.5;
  const cible = versLeClair ? 255 : 0;
  return toHex({
    r: rgb.r + (cible - rgb.r) * amount,
    g: rgb.g + (cible - rgb.g) * amount,
    b: rgb.b + (cible - rgb.b) * amount,
  });
}

/** Contraste minimal WCAG AA pour du texte de taille courante. */
const CONTRASTE_TEXTE = 4.5;

/**
 * La couleur de marque, rendue lisible **en texte** sur un fond donné.
 *
 * C'est le problème qu'a révélé le thème clair, et il touchait déjà la
 * couleur par défaut : le jaune sécurité #F4B41A ne fait que 2:1 sur du
 * papier #F5F5F3.
 * Tous les éléments qui affichent l'accent en texte — liens, libellé actif du
 * menu, montants mis en avant — auraient été à la limite du lisible sur
 * l'écran clair, y compris pour une entreprise n'ayant rien personnalisé.
 *
 * On ne peut pas résoudre ça avec `readableTextOn`, qui choisit entre noir et
 * blanc : ici, c'est *la couleur elle-même* qui doit rester reconnaissable.
 * On la déplace donc vers le noir ou le blanc — selon le fond — juste assez
 * pour franchir le seuil, et pas davantage. La teinte est conservée, seule la
 * clarté bouge.
 */
export function accentInkOn(hex: string, background: string): string {
  const rgb = parseHex(hex);
  const bg = parseHex(background);
  if (!rgb || !bg) return hex;

  // Vers le noir sur fond clair, vers le blanc sur fond sombre.
  const cible = luminance(bg) > 0.5 ? 0 : 255;

  let candidat = rgb;
  // 20 pas de 5 % : assez fin pour ne pas sur-corriger, assez court pour
  // rester une boucle bornée plutôt qu'une recherche.
  for (let i = 0; i <= 20; i++) {
    if (contrast(candidat, bg) >= CONTRASTE_TEXTE) break;
    const t = (i + 1) * 0.05;
    candidat = {
      r: rgb.r + (cible - rgb.r) * t,
      g: rgb.g + (cible - rgb.g) * t,
      b: rgb.b + (cible - rgb.b) * t,
    };
  }
  return toHex(candidat);
}

/**
 * Le fond d'une pastille teintée : l'accent à 15 %, posé sur le fond de page.
 *
 * C'est sur lui, et non sur le fond nu, que l'encre de l'accent doit rester
 * lisible : le filtre actif d'une liste et l'entrée active du menu écrivent
 * l'accent sur l'accent délavé (`bg-accent/15`), et une encre calculée au
 * plus juste pour le fond nu y retombait à 4,1:1. Le fond teinté est plus
 * proche de l'encre que le fond nu, dans les deux thèmes : ce qui passe sur
 * l'un passe sur l'autre.
 */
export function tintedBackground(hex: string, background: string, amount = 0.15): string {
  const rgb = parseHex(hex);
  const bg = parseHex(background);
  if (!rgb || !bg) return background;
  return toHex({
    r: bg.r + (rgb.r - bg.r) * amount,
    g: bg.g + (rgb.g - bg.g) * amount,
    b: bg.b + (rgb.b - bg.b) * amount,
  });
}

/**
 * L'encre de l'accent telle que l'application la pose : calculée sur le fond
 * teinté, le plus exigeant des deux où elle s'écrit. L'aperçu des paramètres
 * l'annonce avec la même fonction, pour ne pas promettre une autre couleur.
 */
export function brandInk(hex: string, background: string): string {
  return accentInkOn(hex, tintedBackground(hex, background));
}

/**
 * Pose la couleur du tenant sur les variables CSS de l'application.
 *
 * Tailwind v4 compile `bg-accent` en `var(--color-accent)` : réécrire la
 * variable suffit donc, sans reconstruire la moindre feuille de style ni
 * recharger la page.
 *
 * Limite connue, vérifiée dans le CSS produit. Les variantes translucides
 * — `bg-accent/15`, `border-accent/20` — sont émises **deux fois** : d'abord
 * l'ambre par défaut figée à la construction, puis un `color-mix` sur la
 * variable, sous `@supports (color: color-mix(...))`. La seconde vient après
 * et l'emporte partout où `color-mix` existe, c'est-à-dire dans tout
 * navigateur depuis le début 2023. Au-delà, les fonds translucides restent
 * ambre pendant que les fonds pleins suivent la couleur du client : une
 * incohérence visuelle, pas une casse.
 */
export function applyBrandColor(
  hex: string | null | undefined,
  /** Le fond de page du thème actuellement affiché. */
  background: string = FONDS[0],
): void {
  const couleur = hex && parseHex(hex) ? hex.toUpperCase() : DEFAULT_PRIMARY_COLOR;
  const root = document.documentElement.style;
  // L'accent « plein » reste la couleur exacte du client : c'est elle qu'on
  // voit sur un bouton, et personne n'a demandé qu'on la retouche.
  root.setProperty("--color-accent", couleur);
  root.setProperty("--color-accent-hover", hoverVariant(couleur));
  root.setProperty("--color-accent-contrast", readableTextOn(couleur));
  // L'accent « encre » est celui qui se pose sur le fond de page. Il dépend
  // donc du thème, contrairement aux trois précédents.
  root.setProperty("--color-accent-ink", brandInk(couleur, background));
}

/** Le fond de page du thème résolu, pour `applyBrandColor`. */
export const PAGE_BACKGROUND: Record<"sombre" | "clair", string> = {
  sombre: FONDS[0],
  clair: FONDS[1],
};

/** Les initiales affichées tant qu'aucun logo n'est déposé. */
export function initialsOf(name: string | null | undefined): string {
  const mots = (name ?? "")
    .split(/[\s-]+/)
    .map((m) => m.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (mots.length === 0) return "?";
  if (mots.length === 1) return mots[0].slice(0, 2).toUpperCase();
  return (mots[0][0] + mots[1][0]).toUpperCase();
}
